import React from "react";
import "./App.css";
import { Suspense } from "react";
import { BrowserRouter as Router, Switch, Route } from "react-router-dom";
import styled from "styled-components";
import { MuiThemeProvider } from "@material-ui/core/styles";
import CssBaseline from "@material-ui/core/CssBaseline";
import LinearProgress from "@material-ui/core/LinearProgress";
import getMuiTheme from "./utils/getMuiTheme";
import getPrefersColorScheme from "./utils/getPrefersColorScheme";
import useDrawer from "./hooks/useDrawer";
import { useCookies } from "react-cookie";
import {
  SITE_PREFERS_COLOR_SCHEME,
  COLOR_SCHEME_CODE_MAP
} from "./resource/constants";
import Drawer from "./components/Drawer/Drawer";
import Root from "./Root";
import Header from "./components/Header/Header";
import Page from "./Layout/Page/Page";
import NavButtonBottom from "./components/NavButtonBottom/NavButtonBottom";
import history from "./history";

export const Home = React.lazy(() => import("./screens/Home"));
export const Projects = React.lazy(() => import("./screens/Projects"));
export const Work = React.lazy(() => import("./screens/Work"));
export const Education = React.lazy(() => import("./screens/Education"));
export const Contact = React.lazy(() => import("./screens/Contact"));
export const Skills = React.lazy(() => import("./screens/Skill"));

export const togglePrefersColorScheme = (
  SELECTED_COLOR_SCHEME,
  setCookie
) => () =>
  setCookie(
    SITE_PREFERS_COLOR_SCHEME,
    SELECTED_COLOR_SCHEME === COLOR_SCHEME_CODE_MAP.LIGHT
      ? COLOR_SCHEME_CODE_MAP.DARK
      : COLOR_SCHEME_CODE_MAP.LIGHT,
    {
      path: "/"
    }
  );

export const Main = styled.div`
  flex: 1 1 auto;
  display: flex;
`;

export default function App({ hideLoader }) {
  const [open, toggleDrawer] = useDrawer();
  const [cookies, setCookie] = useCookies([SITE_PREFERS_COLOR_SCHEME]);

  const SELECTED_COLOR_SCHEME =
    cookies[SITE_PREFERS_COLOR_SCHEME] || getPrefersColorScheme();

  const theme = getMuiTheme(SELECTED_COLOR_SCHEME);

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline>
        <Router history={history}>
          <Drawer open={open} toggleDrawer={toggleDrawer} />
          <Root open={open}>
            <Header
              open={open}
              toggleDrawer={toggleDrawer}
              togglePrefersColorScheme={togglePrefersColorScheme(
                SELECTED_COLOR_SCHEME,
                setCookie
              )}
              theme={SELECTED_COLOR_SCHEME}
            />
            <Main>
              <Page open={open}>
                <Suspense fallback={<LinearProgress color="secondary" />}>
                  <Switch>
                    <Route exact path="/" component={Home} />
                    <Route
                      path="/projects"
                      render={props => (
                        <Projects {...props} theme={theme.palette.type} />
                      )}
                    />
                    <Route
                      path="/work"
                      render={props => (
                        <Work {...props} theme={theme.palette.type} />
                      )}
                    />
                    <Route path="/education" component={Education} />
                    <Route
                      path="/contact"
                      render={props => (
                        <Contact {...props} theme={theme.palette.type} />
                      )}
                    />
                    <Route
                      path="/skills"
                      render={props => (
                        <Skills {...props} theme={theme.palette.type} />
                      )}
                    />
                  </Switch>
                  <NavButtonBottom />
                </Suspense>
              </Page>
            </Main>
          </Root>
        </Router>
      </CssBaseline>
    </MuiThemeProvider>
  );
}
